import React from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  X, Wrench, CheckCircle2, XCircle, Calendar, RotateCcw, BellRing,
} from 'lucide-react';
import { formatDate, getSportColor } from '../../../lib/utils';
import {
  formatBookingReference, formatPaymentMethod, getPaymentMethodColor,
} from '../../../utils/bookingUtils';
import {
  getChipConfig, getSourceConfig, normaliseSource,
  formatBlockLabel,
} from './chipConfig';
import CustomerInfoInline from '../components/CustomerInfoInline';
import { formatTimeRange } from '../BookingSeries/seriesHelpers';
import PaymentAuditPanel from './PaymentAuditPanel';
import { ExternalLink } from 'lucide-react';
import { Link } from 'react-router-dom';

// Read customer info off either the populated `customer_info` block or the
// flat `guest_*` fields. Returns { name, email, phone, type } where `type`
// is "Member" | "Guest".
function getCustomerInfo(booking) {
  const info = booking.customer_info || {};
  if (info.guest_name && info.guest_phone) {
    return {
      name: info.guest_name,
      email: info.guest_email || 'N/A',
      phone: info.guest_phone || 'N/A',
      type: 'Guest',
    };
  }
  if (info.user_name) {
    return {
      name: info.user_name,
      email: info.user_email || 'N/A',
      phone: info.user_phone || 'N/A',
      type: 'Member',
    };
  }
  return {
    name: 'Unknown',
    email: booking.guest_email || 'N/A',
    phone: booking.guest_phone || 'N/A',
    type: booking.guest_phone ? 'Guest' : 'Member',
  };
}

const fmtAuditTs = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    const pad = (n) => String(n).padStart(2, '0');
    return ` · ${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
  } catch {
    return '';
  }
};

export default function BookingCard({
  booking, staffMap, onCancel, onMarkPaid, onRelease, onSendReminder, sendingReminder = false,
  // Optional presentation tweaks — set by the Customer Management page.
  // When shown inside a customer's history, we hide the customer identity
  // row (redundant — the page is that customer) and expose an inline
  // "Open in Bookings →" link so staff can jump to the full-power view
  // to take action.
  hideCustomerRow = false,
  openInBookings = false,
}) {
  const customer = getCustomerInfo(booking);
  const chip = getChipConfig(booking.status);
  const sourceKey = normaliseSource(booking);
  const sourceCfg = getSourceConfig(sourceKey);
  const SourceIcon = sourceCfg.icon;
  const isBlock = sourceKey === 'STAFF_BLOCK';
  const isManual = sourceKey === 'MANUAL_STAFF';
  const isWaitlistOrigin = sourceKey === 'WAITLIST_CONVERSION';
  const showCancel = booking.status === 'AWAITING_PAYMENT';

  // "Cancel & refund" path for CONFIRMED Pocket Pay bookings on the same
  // Brunei day. We show a distinct destructive button that opens the same
  // CancelBookingDialog (the dialog re-validates with the backend and
  // surfaces the auto-void checkbox). Cheap client-side date check =
  // hide-the-button heuristic; backend is the source of truth.
  // Pocket Pay variants: pocket_pay_card / pocket_pay_qr / pocket_pay_biller —
  // all share the prefix so `.startsWith()` covers every mode.
  const isPocketPay = (booking.payment_method || '').toLowerCase().startsWith('pocket_pay');
  const paidAtIso = booking.payment_completed_at;
  const isSameBruneiDayAsToday = (() => {
    if (!paidAtIso) return false;
    try {
      // Brunei is UTC+8 with no DST — extract YYYY-MM-DD in BN time.
      const paidUtcMs = new Date(paidAtIso).getTime();
      const paidBn = new Date(paidUtcMs + 8 * 3600 * 1000).toISOString().slice(0, 10);
      const nowBn = new Date(Date.now() + 8 * 3600 * 1000).toISOString().slice(0, 10);
      return paidBn === nowBn;
    } catch { return false; }
  })();
  const showVoidAndCancel = isPocketPay
    && booking.status === 'CONFIRMED'
    && !booking.voided_at
    && isSameBruneiDayAsToday;

  const resolveStaffName = (id) => id
    ? (staffMap[id] || `staff:${String(id).slice(0, 8)}`)
    : null;
  const createdById = booking.created_by_staff;
  const createdByName = createdById ? resolveStaffName(createdById) : null;
  const paidByName = resolveStaffName(booking.marked_paid_by_staff);
  // `released_by_staff_name` is written directly by release_service.py
  // (not an id to resolve via staffMap — the name is already known at
  // release time), unlike created_by_staff/marked_paid_by_staff/etc.
  const releasedByName = booking.released_by_staff_name
    || resolveStaffName(booking.released_by_staff_id);
  const showPaidAudit = booking.payment_status === 'paid'
    && paidByName
    && (booking.payment_method || '').toLowerCase() !== 'online';
  // Pocket Pay confirmed bookings show a chip with the paid-on timestamp so
  // admins can eyeball "is this same-day?" without opening details. The
  // existing `showPaidAudit` chip only fires for offline/manual mark-paid;
  // pocket-pay charges flow through the webhook (`mark_booking_paid_online`)
  // with no `marked_paid_by_staff`, so we render a separate variant.
  const showPocketPayPaidChip = isPocketPay
    && booking.status === 'CONFIRMED'
    && booking.payment_completed_at
    && !booking.voided_at;
  const showVoidedChip = !!booking.voided_at;
  // Refund-status chips for CANCELLED bookings (Feb 2026):
  //   • Green "Refunded via Pocket Pay" → cancelled + voided (auto-refund happened)
  //   • Amber "Refund pending"          → cancelled + was paid + no void recorded
  //     (offline payment OR Pocket Pay void that hasn't been triggered yet —
  //     either way, money needs to be returned manually).
  // Unpaid cancellations (AWAITING_PAYMENT → CANCELLED, Pay-Later released)
  // get neither chip — no money to refund.
  const wasPaid = booking.payment_status === 'paid'
    || !!booking.payment_completed_at;
  const showRefundedViaPocketPayChip = booking.status === 'CANCELLED'
    && booking.voided_at
    && isPocketPay;
  const showRefundPendingChip = booking.status === 'CANCELLED'
    && wasPaid
    && !booking.voided_at;
  // Cancellation-attribution chip: shows on any CANCELLED booking that
  // isn't a void (voids already get their own chip explaining the
  // refund — a separate "Cancelled" chip alongside would be noise).
  const showCancelledByChip = booking.status === 'CANCELLED' && !booking.voided_at;
  // Attribution label for the "Cancelled by …" chip.
  // Preferred: resolve `cancelled_by_id` (new field, mirrors created_by_staff /
  // discount_by / marked_paid_by_staff) against staffMap to get the real
  // display name → "Cancelled by Andy Lau".
  // Fallback: the legacy `cancelled_by` enum string ("customer"/"admin"/
  // "system"/"admin:email") for rows cancelled before the id field existed.
  const cancelledByLabel = (() => {
    const cb = (booking.cancelled_by || '').toLowerCase();
    if (cb === 'customer') return 'Customer';
    if (cb === 'system') return 'System';
    // Admin / manager path — resolve the id to a real display name when we have it.
    const byId = booking.cancelled_by_id
      ? resolveStaffName(booking.cancelled_by_id)
      : null;
    if (byId && !byId.startsWith('staff:')) return byId;
    if (cb.startsWith('admin') || cb === 'manager') return 'Admin';
    return cb || 'Unknown';
  })();
  const showReleasedAudit = booking.status === 'RELEASED' && !!releasedByName;

  const showPayLater = booking.payment_status === 'pending'
    && booking.status !== 'CANCELLED'
    && (onMarkPaid || onRelease);

  const grossTotal = booking.gross_total || 0;
  const finalTotal = booking.final_total ?? booking.estimated_total ?? 0;
  const showDiscount = grossTotal > 0 && booking.discount_value > 0;

  return (
    <Card
      className={`px-4 py-3 space-y-2 ${sourceCfg.cardAccent}`}
      data-testid="booking-card"
      data-source={sourceKey}
    >
      {/* Row 1 — header pills + price */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="font-mono text-xs px-2 py-0.5 rounded bg-gray-100 text-gray-700">
            {formatBookingReference(booking.id)}
          </span>
          <span className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${chip.countBadge}`}>
            {chip.label}
          </span>
          <span
            className={`inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full ${sourceCfg.pillBg}`}
            title={`Source: ${sourceCfg.label}`}
            data-testid={`source-pill-${sourceKey.toLowerCase()}`}
          >
            <SourceIcon className="w-3 h-3" />
            {sourceCfg.label}
          </span>
          {booking.payment_method && !isBlock && (
            <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
              getPaymentMethodColor(booking.payment_method) === 'blue' ? 'bg-blue-100 text-blue-700'
                : getPaymentMethodColor(booking.payment_method) === 'green' ? 'bg-green-100 text-green-700'
                : 'bg-gray-100 text-gray-700'
            }`}>
              {formatPaymentMethod(booking.payment_method)}
            </span>
          )}
          {booking.duration && booking.duration > 1 && (
            <span className="text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full bg-accent/20 text-accent-foreground">
              {booking.duration}h
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <PriceInline isBlock={isBlock} grossTotal={grossTotal} finalTotal={finalTotal} showDiscount={showDiscount} booking={booking} resolveStaffName={resolveStaffName} />
          {openInBookings && booking.id && (
            <Link
              to={`/admin/bookings?ref=${(booking.id || '').slice(0, 8)}`}
              className="inline-flex items-center gap-1 text-xs text-primary hover:underline"
              data-testid={`open-in-bookings-${(booking.id || '').slice(0, 8)}`}
              title="Open this booking in the Bookings page"
            >
              <ExternalLink className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Open</span>
            </Link>
          )}
        </div>
      </div>

      {/* Row 2 — date/time · customer (or "Reserved For" block) */}
      <div className="grid grid-cols-1 md:grid-cols-12 gap-x-4 gap-y-1 text-sm border-t pt-2">
        <div className="md:col-span-4 inline-flex items-center gap-2 font-semibold text-gray-900">
          <Calendar className="w-4 h-4 text-gray-700" />
          {formatDate(booking.booking_date)} · {formatTimeRange(booking.time_slot, booking.duration)}
        </div>
        <div className="md:col-span-8">
          {isBlock ? (
            <div className="inline-flex items-center gap-2 text-sm">
              <Wrench className="w-4 h-4 text-slate-600" />
              <span className="font-semibold text-slate-900">{formatBlockLabel(booking.block_label)}</span>
              {booking.block_reason && (
                <span className="text-slate-600 italic truncate" title={booking.block_reason}>
                  · &quot;{booking.block_reason}&quot;
                </span>
              )}
            </div>
          ) : hideCustomerRow ? (
            // In Customer Management: the customer identity is already
            // rendered at the top of the page; showing it again per
            // booking is noise. Fall back to a compact location line
            // so the row still has useful context.
            booking.location_name ? (
              <div className="text-sm text-muted-foreground">{booking.location_name}</div>
            ) : null
          ) : (
            <CustomerInfoInline customer={customer} testId={`booking-customer-${booking.id?.slice(0, 8)}`} />
          )}
        </div>
      </div>

      {/* Row 3 — courts + actions */}
      <div className="flex items-center justify-between gap-3 flex-wrap border-t pt-2">
        <CourtsInline booking={booking} />
        <div className="flex items-center gap-2 flex-wrap">
          {(isManual || isBlock || isWaitlistOrigin) && createdByName && (
            <span className="text-[10px] text-muted-foreground italic">
              by {createdByName}
            </span>
          )}
          {showCancel && (
            <Button
              size="sm"
              variant="destructive"
              onClick={() => onCancel(booking.id)}
              data-testid="cancel-booking-btn"
            >
              <X className="w-3.5 h-3.5 mr-1" /> Cancel
            </Button>
          )}
          {showVoidAndCancel && (
            <Button
              size="sm"
              variant="outline"
              className="border-amber-300 text-amber-900 hover:bg-amber-50"
              onClick={() => onCancel(booking.id)}
              title="Same-day Pocket Pay: cancel and void the charge"
              data-testid="void-pocketpay-btn"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1" /> Cancel &amp; Refund
            </Button>
          )}
        </div>
      </div>

      {/* Row 4 (conditional) — Pay Later actions */}
      {showPayLater && (
        <div
          className="border-t pt-2 flex items-center justify-between gap-3 flex-wrap rounded-md bg-amber-50 border border-amber-300 px-3 py-2"
          data-testid="pay-later-actions"
        >
          <div className="text-xs text-amber-900">
            <strong>⏳ Payment Pending</strong> — held until paid or released
          </div>
          <div className="flex gap-2">
            {onMarkPaid && (
              <Button
                size="sm"
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                onClick={() => onMarkPaid(booking)}
                data-testid="mark-paid-btn"
              >
                <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Mark Paid
              </Button>
            )}
            {onRelease && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onRelease(booking)}
                data-testid="release-slot-btn"
              >
                <XCircle className="w-3.5 h-3.5 mr-1" /> Release
              </Button>
            )}
            {onSendReminder && (
              <Button
                size="sm"
                variant="outline"
                className="text-amber-700 border-amber-300 hover:bg-amber-100"
                onClick={() => onSendReminder(booking)}
                disabled={sendingReminder}
                data-testid={`send-reminder-btn-${booking.id}`}
              >
                <BellRing className="w-3.5 h-3.5 mr-1" /> {sendingReminder ? 'Sending…' : 'Send Reminder'}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Row 5 (conditional) — Audit chips */}
      {(showPaidAudit || showReleasedAudit || showPocketPayPaidChip || showVoidedChip || showCancelledByChip || showRefundedViaPocketPayChip || showRefundPendingChip || booking.pocket_pay_order_id || booking.pocket_pay_order_ref || booking.payment_reference) && (
        <div className="border-t pt-2 flex flex-wrap gap-2" data-testid="audit-chips">
          {showPaidAudit && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200"
              data-testid="paid-by-chip"
            >
              <CheckCircle2 className="w-3 h-3" />
              Paid by {paidByName}{fmtAuditTs(booking.payment_completed_at)}
            </span>
          )}
          {/* Staff-entered payment reference (cash/bank-transfer/complimentary
              "Mark as Paid" dialog). Mirrors the Pocket Pay txn-ref chip
              style so the audit row reads consistently. Truncated to 40
              chars inline; full text in tooltip.

              Suppressed when its value equals `pocket_pay_order_id` —
              for Pocket Pay bookings the success callback sets
              `payment_reference = order_id`, which would otherwise
              produce two identical "Ref: 21474897xx" chips next to each
              other (see Feb 2026 display bug). The dedicated
              `pocketpay-txn-ref-chip` below handles the gateway case. */}
          {booking.payment_reference
            && booking.payment_reference !== booking.pocket_pay_order_id && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-slate-100 text-slate-700 border border-slate-200 max-w-[280px]"
              data-testid="payment-reference-chip"
              title={booking.payment_reference}
            >
              <span className="font-semibold">Ref:</span>
              <span className="truncate">{booking.payment_reference}</span>
            </span>
          )}
          {showPocketPayPaidChip && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-100 text-blue-800 border border-blue-200"
              data-testid="pocketpay-paid-chip"
              title="Paid via Pocket Pay gateway"
            >
              <CheckCircle2 className="w-3 h-3" />
              Pocket Pay{fmtAuditTs(booking.payment_completed_at)}
            </span>
          )}
          {/* Pocket Pay transaction reference — separate chip so admins can
              cross-reference with the gateway dashboard without opening the
              booking detail page. Shown on any booking that carries an order
              id, even after void / cancel, so the trail stays visible. */}
          {booking.pocket_pay_order_id && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono bg-slate-100 text-slate-700 border border-slate-200"
              data-testid="pocketpay-txn-ref-chip"
              title="Pocket Pay transaction reference (use this to look up the charge in the gateway dashboard)"
            >
              Ref: {booking.pocket_pay_order_id}
            </span>
          )}
          {/* Pocket Pay's bank-side `order_ref` — smaller companion to
              the order_id chip. Empty on legacy bookings (pre-June-2026)
              because we only started capturing this from /payments/status
              after the audit-trail broadening. Skipped when it duplicates
              the order_id (defensive — Pocket Pay always returns distinct
              values but let's not double-chip if the invariant slips). */}
          {booking.pocket_pay_order_ref
            && booking.pocket_pay_order_ref !== booking.pocket_pay_order_id && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-mono bg-slate-50 text-slate-600 border border-slate-200"
              data-testid="pocketpay-order-ref-chip"
              title="Pocket Pay internal order_ref — the bank-side reference returned by /payments/status. Useful for gateway audits and dispute correlation."
            >
              PP Ref: {booking.pocket_pay_order_ref}
            </span>
          )}
          {showVoidedChip && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-purple-100 text-purple-800 border border-purple-200"
              data-testid="voided-chip"
              title={booking.void_reason || 'Pocket Pay payment was voided'}
            >
              <RotateCcw className="w-3 h-3" />
              Voided{fmtAuditTs(booking.voided_at)}
            </span>
          )}
          {showRefundedViaPocketPayChip && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-emerald-100 text-emerald-800 border border-emerald-200"
              data-testid="refunded-pocketpay-chip"
              title="Charge was automatically voided through Pocket Pay; funds returned to the cardholder."
            >
              <CheckCircle2 className="w-3 h-3" />
              Refunded via Pocket Pay
            </span>
          )}
          {showRefundPendingChip && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-amber-100 text-amber-800 border border-amber-200"
              data-testid="refund-pending-chip"
              title="Booking was cancelled after payment was collected. Refund must be processed manually."
            >
              <RotateCcw className="w-3 h-3" />
              Refund pending
            </span>
          )}
          {showCancelledByChip && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-slate-100 text-slate-700 border border-slate-200"
              data-testid="cancelled-by-chip"
              title={booking.cancellation_reason || 'No reason provided'}
            >
              <XCircle className="w-3 h-3" />
              Cancelled by {cancelledByLabel}{fmtAuditTs(booking.cancelled_at)}
            </span>
          )}
          {showReleasedAudit && (
            <span
              className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold bg-rose-100 text-rose-800 border border-rose-200 max-w-[320px]"
              data-testid="released-by-chip"
              title={booking.released_note || ''}
            >
              <XCircle className="w-3 h-3 shrink-0" />
              <span>Released by {releasedByName}{fmtAuditTs(booking.released_at)}</span>
              {booking.released_note && (
                <span className="opacity-70 truncate">· &quot;{booking.released_note}&quot;</span>
              )}
            </span>
          )}
        </div>
      )}

      {/* Row 6 (conditional) — Special requests / cancellation reason */}
      {booking.special_requests && (
        <div className="border-t pt-2 text-xs text-gray-700 italic">
          <span className="font-semibold not-italic text-gray-900 mr-1">Note:</span>
          &quot;{booking.special_requests}&quot;
        </div>
      )}
      {booking.status === 'CANCELLED' && booking.cancellation_reason && (
        <div className="border-t pt-2 text-xs text-slate-700" data-testid="cancellation-reason">
          <span className="font-semibold text-slate-900 mr-1">Cancellation reason:</span>
          <span className="italic">&quot;{booking.cancellation_reason}&quot;</span>
        </div>
      )}

      {/* Payment Audit panel — only mounted for Pocket Pay bookings. Lazy
          loads the callback ledger on first expand; never on list render. */}
      {booking.pocket_pay_order_id && (
        <PaymentAuditPanel bookingId={booking.id} />
      )}
    </Card>
  );
}

function PriceInline({ isBlock, grossTotal, finalTotal, showDiscount, booking, resolveStaffName }) {
  if (isBlock) {
    return (
      <span className="text-xs font-semibold text-slate-700">No charge</span>
    );
  }
  const discountAmount = Math.max(0, (grossTotal || 0) - (finalTotal || 0));
  // Historical assign_courts bookings never captured an admin id, so fall
  // back to a generic "Staff" attribution per product call (admin-only,
  // honest about legacy data).
  const discountByName = booking.discount_by
    ? (resolveStaffName ? resolveStaffName(booking.discount_by) : null)
    : null;
  const attribution = discountByName || 'Staff';
  const reason = (booking.discount_reason || '').trim();
  const reasonClean = reason && reason.toLowerCase() !== 'no reason provided' ? reason : '';
  return (
    <div className="flex flex-col items-end leading-tight">
      {showDiscount ? (
        <>
          <span className="text-[11px] text-muted-foreground line-through">
            Gross ${grossTotal.toFixed(2)}
          </span>
          <span className="font-bold text-lg text-primary" title={`Discount: ${booking.discount_type === 'percentage' ? `${booking.discount_value}%` : `$${booking.discount_value}`}`}>
            Net ${finalTotal.toFixed(2)}
          </span>
          <span
            className="mt-0.5 inline-flex items-center gap-1 text-[10px] font-medium px-1.5 py-0.5 rounded-full bg-amber-50 text-amber-800 border border-amber-200"
            data-testid="discount-attribution-chip"
            title={reasonClean ? `Reason: ${reasonClean}` : undefined}
          >
            −${discountAmount.toFixed(2)} by {attribution}
            {reasonClean ? <span className="opacity-70">· {reasonClean}</span> : null}
          </span>
        </>
      ) : (
        <span className="font-bold text-lg">${finalTotal.toFixed(2)}</span>
      )}
    </div>
  );
}

function CourtsInline({ booking }) {
  const assigned = booking.courts_assigned;
  const requested = booking.courts_requested;
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-[10px] uppercase font-bold text-muted-foreground">
        Courts:
      </span>
      {assigned && assigned.length > 0
        ? assigned.map((c, idx) => (
            <span
              key={`a-${idx}-${c.court_number}-${c.sport}`}
              className={`sport-badge text-xs ${getSportColor(c.sport)}`}
            >
              {c.court_number} · {c.sport}
            </span>
          ))
        : requested?.map((c, idx) => (
            <span
              key={`r-${idx}-${c.sport}`}
              className={`sport-badge text-xs ${getSportColor(c.sport)}`}
            >
              {c.sport}
            </span>
          ))}
    </div>
  );
}
